"use client";

import Link from "next/link";
import { useState, useSyncExternalStore } from "react";
import { usePalette } from "@/components/theme/PaletteProvider";
import {
  AlertTriangle,
  ArrowRight,
  ArrowUpRight,
  BookOpenText,
  CheckCircle2,
  FileDiff,
  Gauge,
  ImageDown,
  Lock,
  Scissors,
  Sparkles,
  Boxes,
  Workflow,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToolAccess } from "@/lib/home/planPreview";
import type { FeatureKey } from "@/components/auth/AuthProvider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useServiceSettings } from "@/hooks/useSiteSettings";
import type { ServiceKey } from "@/hooks/useSiteSettings";
import { studioLockCopy } from "@/components/hub/hubData";
import { hasFeature, lockedToolHref } from "@/lib/billing/planLabel";
import { resolveToolPreviewImage } from "@/lib/toolPreviewImages";
import { cn } from "@/lib/utils";

/**
 * Canonical "just shipped" list for the New Feature row.
 *
 * Single source of truth for "what counts as new". The marketing landing
 * renders this as a full banner row (to excite prospects); the signed-in
 * subscriber home consumes the derived {@link justShippedRank} instead, to
 * float these tools to the front of its All-tools grid with a distinct
 * marker — no announcement band. Order is newest-first. When a tool graduates
 * out of "new", drop it here: the marketing row loses the card and the grid
 * reverts the tool to its evergreen hub badge automatically.
 */
export type NewFeatureTool = {
  /** ServiceKey, used to resolve the live preview image. */
  key: ServiceKey;
  /** Feature gate, used to decide the locked state on the signed-in home. */
  featureKey: FeatureKey;
  title: string;
  blurb: string;
  href: string;
  icon: LucideIcon;
  fileTags: string[];
  /** Short, checkmarked selling points shown in the tool's detail popup. */
  features: string[];
  /** Fallback gradient shown behind the icon when no preview image exists. */
  gradient: string;
};

export const NEW_FEATURE_TOOLS: NewFeatureTool[] = [
  {
    key: "inklab",
    featureKey: "inklab",
    title: "Ink Lab",
    blurb: "Make a custom freemode tattoo from a prompt, a reference or your own PNG, and export a drop-in FiveM addon.",
    href: "/ink-lab",
    icon: Sparkles,
    fileTags: [".png", ".webp"],
    features: [
      "Generate art with AI or upload your own PNG",
      "Preview it on the freemode body and build tattoo packs",
      "Export a drop-in FiveM addon or overlay",
    ],
    gradient: "from-violet-500/25 to-rose-600/25",
  },
  {
    key: "modkitviewer",
    featureKey: "modkitviewer",
    title: "Modkit Viewer",
    blurb: "Preview kit parts per category, toggle extras and open doors on your own vehicle. Files stay on your device.",
    href: "/modkit-viewer",
    icon: Boxes,
    fileTags: [".yft", ".meta"],
    features: [
      "Preview kit parts per category, with YTD textures",
      "Toggle extras and open doors, custom door bones included",
      "Save builds as JSON. Files never leave your device",
    ],
    gradient: "from-sky-500/25 to-indigo-600/25",
  },
  {
    key: "blinklab3d",
    featureKey: "blinklab3d",
    title: "3D BlinkLab",
    blurb: "Design a siren pattern and watch it play on your own .yft in 3D.",
    href: "/blinklab-3d",
    icon: Zap,
    fileTags: [".yft", ".xml", ".json"],
    features: [
      "Live siren playback on your own .yft",
      "Shares projects with Blink Lab",
      "Export carcols.meta-ready sequences",
    ],
    gradient: "from-red-500/25 to-blue-600/25",
  },
  {
    key: "handlingdiff",
    featureKey: "handlingdiff",
    title: "Handling Diff",
    blurb: "Compare two handling.meta files field by field, with a header count that never lies.",
    href: "/handling-diff",
    icon: FileDiff,
    fileTags: [".meta"],
    features: [
      "Field-by-field handling.meta comparison",
      "Threshold-aware — float noise never reads as a change",
      "Flags unrecognised fields the game silently ignores",
    ],
    gradient: "from-amber-500/25 to-stone-600/25",
  },
  {
    key: "resmon",
    featureKey: "resmon",
    title: "Resmon Report",
    blurb: "Turn a resmon or txAdmin paste into a ranked, per-resource performance report.",
    href: "/resmon",
    icon: Gauge,
    fileTags: [".txt", ".csv", ".json"],
    features: [
      "Ranks resources by per-frame CPU cost",
      "Reconciles against the dump's declared total",
      "Surfaces unrecognised lines instead of hiding them",
    ],
    gradient: "from-cyan-500/25 to-slate-600/25",
  },
  {
    key: "embroiderytool",
    featureKey: "embroiderytool",
    title: "Embroidery Tool",
    blurb: "Turn uploaded logos and patch art into stitched, embroidery-style textures.",
    href: "/embroidery-tool",
    icon: Scissors,
    fileTags: [".png", ".webp", ".jpg"],
    features: [
      "Upload logos, badges, icons, or patch art",
      "AI converts artwork into stitched thread texture",
      "Saves outputs to a dedicated embroidery history",
    ],
    gradient: "from-rose-500/25 to-violet-600/25",
  },
  {
    key: "chevronbuilder",
    featureKey: "chevronbuilder",
    title: "Chevron Builder",
    blurb: "Generate print-ready rear warning chevrons for Fire, EMS, DOT, and tow.",
    href: "/chevron-builder",
    icon: AlertTriangle,
    fileTags: [".png"],
    features: [
      "Print-ready rear warning chevrons",
      "Presets for Fire, EMS, DOT & tow",
      "Exports high-resolution .png files",
    ],
    gradient: "from-amber-500/25 to-red-600/25",
  },
  {
    key: "codevisualizer",
    featureKey: "codevisualizer",
    title: "Code Visualizer",
    blurb: "Map FiveM scripts into readable blocks for events, commands, and exports.",
    href: "/code-visualizer",
    icon: Workflow,
    fileTags: [".lua", ".js", ".zip"],
    features: [
      "Map scripts into readable blocks",
      "Surfaces events, commands & exports",
      "Works on .lua and .js resources",
    ],
    gradient: "from-cyan-500/25 to-blue-500/25",
  },
  {
    key: "scriptdocs",
    featureKey: "scriptdocs",
    title: "Script Docs",
    blurb: "Generate README-ready docs from resource scripts and manifests.",
    href: "/script-docs",
    icon: BookOpenText,
    fileTags: [".lua", ".md", ".zip"],
    features: [
      "Auto-generate README-ready docs",
      "Reads resource scripts & manifests",
      "Exports clean Markdown",
    ],
    gradient: "from-sky-500/25 to-emerald-500/25",
  },
  {
    key: "creatorconvert",
    featureKey: "creatorconvert",
    title: "Creator Convert",
    blurb: "Convert DDS textures, create DDS files, or extract YTDs.",
    href: "/creator-convert",
    icon: ImageDown,
    fileTags: [".dds", ".ytd", ".zip", ".png", ".webp", ".jpg"],
    features: [
      "Convert DDS textures and create DDS from images",
      "Extract selected YTD textures into a ZIP",
      "Runs in the browser, no install",
    ],
    gradient: "from-orange-500/25 to-stone-600/25",
  },
];

/** The row shows one line of cards (4-up on desktop); older entries stay in the
 *  "just shipped" set for the hub grid but drop off this row. */
const NEW_FEATURE_ROW_LIMIT = 4;

/** Newest-first hrefs of the rotating "just shipped" set. */
export const NEW_FEATURE_HREFS: readonly string[] = NEW_FEATURE_TOOLS.map((t) => t.href);

/**
 * Newest-first rank of an href within the rotating "just shipped" set, or -1
 * when the tool is not currently new. The signed-in grid (CreatorHome) uses
 * this to float recently-shipped tools to the front of the All-tools grid and
 * give them a distinct accent marker. Because it reads the same source as the
 * marketing row, the boost + marker rotate out automatically the moment a tool
 * is dropped from {@link NEW_FEATURE_TOOLS} — nothing else to maintain.
 */
export function justShippedRank(href: string): number {
  return NEW_FEATURE_HREFS.indexOf(href);
}

type Variant = "landbook" | "hub";

// Per-page class bundles so the row blends into each homepage's palette: the
// marketing landing uses the warm "landbook" tokens, the signed-in home uses
// the shared semantic (shadcn) tokens.
const THEME: Record<
  Variant,
  {
    eyebrow: string;
    title: string;
    subtitle: string;
    viewAll: string;
    card: string;
    iconChip: string;
    cardTitle: string;
    cardBlurb: string;
    fileTag: string;
    arrow: string;
    newPill: string;
    lockOverlay: string;
    // Detail popup tokens
    /**
     * Token scope applied to the portaled DialogContent. Radix portals render to
     * `document.body`, OUTSIDE the page's `.lb-page` element, so the warm
     * `--lb-*` variables don't cascade in. `.lb-tokens` re-declares them on the
     * modal itself; "hub" uses semantic shadcn tokens that already exist on
     * `:root`, so it needs no scope.
     */
    modalScope: string;
    modalSurface: string;
    modalChip: string;
    modalCheck: string;
    modalFeature: string;
    cta: string;
  }
> = {
  landbook: {
    eyebrow: "text-[hsl(var(--lb-muted))]",
    title: "text-[hsl(var(--lb-ink))]",
    subtitle: "text-[hsl(var(--lb-muted))]",
    viewAll: "text-[hsl(var(--lb-muted))] hover:text-[hsl(var(--lb-ink))]",
    card: "border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-panel)/0.5)] hover:border-[hsl(var(--lb-ink)/0.3)] hover:bg-[hsl(var(--lb-panel))]",
    iconChip:
      "border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-bg-soft))] text-[hsl(var(--lb-muted))] group-hover:border-[hsl(var(--lb-ink)/0.3)] group-hover:bg-[hsl(var(--lb-ink))] group-hover:text-[hsl(var(--lb-bg))]",
    cardTitle: "text-[hsl(var(--lb-ink))]",
    cardBlurb: "text-[hsl(var(--lb-muted))]",
    fileTag: "border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] text-success",
    arrow: "text-[hsl(var(--lb-muted))]",
    newPill: "border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] text-success",
    lockOverlay: "bg-[hsl(var(--lb-panel)/0.6)] text-[hsl(var(--lb-ink))]",
    modalScope: "lb-tokens",
    modalSurface: "border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-bg))] text-[hsl(var(--lb-ink))]",
    modalChip: "border-[hsl(var(--lb-line))] bg-[hsl(var(--lb-bg-soft))] text-[hsl(var(--lb-muted))]",
    modalCheck: "text-[hsl(var(--lb-muted))]",
    modalFeature: "text-[hsl(var(--lb-muted))]",
    cta: "bg-[hsl(var(--lb-ink))] text-[hsl(var(--lb-bg))] hover:opacity-90",
  },
  hub: {
    eyebrow: "text-muted-foreground",
    title: "text-foreground",
    subtitle: "text-muted-foreground",
    viewAll: "text-muted-foreground hover:text-foreground",
    card: "border-border/70 bg-card/80 hover:border-accent/40 hover:bg-accent/5",
    iconChip:
      "border-border/70 bg-secondary/60 text-foreground/85 group-hover:border-accent/35 group-hover:text-accent",
    cardTitle: "text-foreground",
    cardBlurb: "text-muted-foreground",
    fileTag: "border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.1)] text-success",
    arrow: "text-muted-foreground",
    newPill:
      "border-[hsl(var(--success)/0.45)] bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]",
    lockOverlay: "bg-background/55 text-foreground",
    modalScope: "",
    modalSurface: "border-border/70 bg-card text-foreground",
    modalChip: "border-border/70 bg-secondary/50 text-muted-foreground",
    modalCheck: "text-muted-foreground",
    modalFeature: "text-muted-foreground",
    cta: "bg-accent text-accent-foreground hover:opacity-90",
  },
};

function NewPill({ className }: { className: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center rounded-full border px-2 font-mono text-[8px] uppercase leading-none tracking-[0.16em]",
        className,
      )}
    >
      New
    </span>
  );
}

/**
 * "New Tools" banner row for the marketing landing. Renders the
 * recently-shipped tools as a 4-up grid of preview cards (scrolls on mobile).
 *
 * Self-sufficient: it reads live preview images and the viewer's entitlements
 * straight from the shared hooks, so the only thing a page passes is the
 * `variant` (palette) and whether to dim locked tools.
 *
 * Clicking a card opens a detail popup (preview image, description, feature
 * list, and an "Open <tool>" CTA) rather than navigating straight away — the
 * CTA inside the popup is the real link, so locked tools route to pricing.
 *
 * The signed-in subscriber home deliberately does NOT render this band — it
 * surfaces new tools inline in its All-tools grid via {@link justShippedRank}
 * instead, so it never costs a dedicated section of vertical space.
 *
 * @param variant   "landbook" for the marketing landing, "hub" if ever reused
 *                   on a signed-in surface.
 * @param showLocked When true, tools the viewer can't open are dimmed with a
 *                    lock overlay and the popup CTA routes to pricing. When
 *                    false, every CTA opens the tool (marketing default).
 */
const noopSubscribe = () => () => {};

export function NewFeatureRow({
  variant,
  showLocked = false,
}: {
  variant: Variant;
  showLocked?: boolean;
}) {
  const { permissions, isAdmin } = useToolAccess();
  const { data: serviceSettings = [] } = useServiceSettings();
  const { palette: selectedPalette } = usePalette();
  const hydrated = useSyncExternalStore(noopSubscribe, () => true, () => false);
  const previewPalette = hydrated ? selectedPalette : "classic";
  const theme = THEME[variant];
  const [openTool, setOpenTool] = useState<NewFeatureTool | null>(null);

  if (NEW_FEATURE_TOOLS.length === 0) return null;

  const isLocked = (tool: NewFeatureTool) =>
    showLocked && !hasFeature(permissions, tool.featureKey, { isAdmin });

  const activeLocked = openTool ? isLocked(openTool) : false;
  const activeImage = openTool
    ? resolveToolPreviewImage({
      palette: previewPalette,
        href: openTool.href,
        featureKey: openTool.featureKey,
        serviceSettings,
      })
    : null;
  const ActiveIcon = openTool?.icon;

  return (
    <section className="py-8 sm:py-12">
      <div className="lb-container">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <NewPill className={theme.newPill} />
              <p
                className={cn(
                  "font-mono text-[11px] uppercase tracking-[0.28em]",
                  theme.eyebrow,
                )}
              >
                Just released
              </p>
            </div>
            <h2
              className={cn(
                "mt-2 text-2xl font-semibold tracking-[-0.04em] sm:text-[2rem]",
                theme.title,
              )}
            >
              New Tools in The Workshop
            </h2>
          </div>
          <Link
            href="#tools"
            className={cn(
              "group hidden items-center gap-1 text-sm font-medium transition sm:inline-flex",
              theme.viewAll,
            )}
          >
            Browse all tools
            <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
          </Link>
        </div>

        {/* Cards: horizontal scroll on mobile, 4-up grid on desktop */}
        <div className="mt-6 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-2 sm:overflow-visible sm:pb-0 lg:grid-cols-4">
          {NEW_FEATURE_TOOLS.slice(0, NEW_FEATURE_ROW_LIMIT).map((tool) => {
            const locked = isLocked(tool);
            const imageUrl = resolveToolPreviewImage({
      palette: previewPalette,
              href: tool.href,
              featureKey: tool.featureKey,
              serviceSettings,
            });
            const Icon = tool.icon;

            return (
              <button
                key={tool.href}
                type="button"
                onClick={() => setOpenTool(tool)}
                aria-haspopup="dialog"
                className={cn(
                  "group relative flex min-w-[15rem] flex-none snap-start flex-col overflow-hidden rounded-2xl border text-left transition duration-200 hover:-translate-y-0.5 sm:min-w-0 sm:flex-1",
                  theme.card,
                )}
              >
                {/* Preview banner */}
                <div className="relative aspect-[16/7] overflow-hidden">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      aria-hidden
                      className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className={cn("absolute inset-0 bg-gradient-to-br", tool.gradient)} />
                  )}
                  {/* Icon chip floats over the banner */}
                  <span
                    className={cn(
                      "absolute bottom-2 left-2 grid h-9 w-9 place-items-center rounded-xl border backdrop-blur-sm transition duration-200",
                      theme.iconChip,
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  {locked && (
                    <div
                      className={cn(
                        "absolute inset-0 grid place-items-center backdrop-blur-[2px]",
                        theme.lockOverlay,
                      )}
                    >
                      <Lock className="h-4 w-4" />
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="flex flex-1 flex-col gap-2 p-4">
                  <div className="flex items-center gap-1.5">
                    <p className={cn("text-base font-semibold tracking-tight", theme.cardTitle)}>
                      {tool.title}
                    </p>
                    <NewPill className={theme.newPill} />
                  </div>
                  <p className={cn("line-clamp-2 flex-1 text-[12.5px] leading-relaxed", theme.cardBlurb)}>
                    {tool.blurb}
                  </p>
                  <div className="flex flex-wrap items-center gap-1">
                    {tool.fileTags.map((tag) => (
                      <span
                        key={tag}
                        className={cn(
                          "inline-flex items-center rounded border px-1.5 py-px font-mono text-[9px] tracking-wide",
                          theme.fileTag,
                        )}
                      >
                        {tag}
                      </span>
                    ))}
                    <ArrowUpRight
                      className={cn(
                        "ml-auto h-4 w-4 -translate-x-1 opacity-0 transition duration-300 group-hover:translate-x-0 group-hover:opacity-100",
                        theme.arrow,
                      )}
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tool detail popup — fired by clicking any card above */}
      <Dialog open={!!openTool} onOpenChange={(open) => !open && setOpenTool(null)}>
        {openTool && (
          <DialogContent
            className={cn(
              "block w-[calc(100%-2rem)] max-w-xl gap-0 overflow-hidden rounded-2xl border p-0 sm:rounded-2xl",
              theme.modalScope,
              theme.modalSurface,
            )}
          >
            {/* Preview banner */}
            <div className="relative aspect-[16/9] w-full overflow-hidden">
              {activeImage ? (
                <img
                  src={activeImage}
                  alt={`${openTool.title} preview`}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className={cn("absolute inset-0 bg-gradient-to-br", openTool.gradient)} />
              )}
              {/* Scrim so the shared top-right close (X) stays legible over busy
                  or light preview images. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/45 to-transparent"
              />
              {/* Icon chip floats over the banner */}
              <span
                className={cn(
                  "absolute bottom-3 left-3 grid h-10 w-10 place-items-center rounded-xl border backdrop-blur-sm",
                  theme.iconChip,
                )}
              >
                {ActiveIcon ? <ActiveIcon className="h-5 w-5" /> : null}
              </span>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-3.5 p-6 sm:p-7">
              <div className="flex items-center gap-2">
                <DialogTitle className="text-2xl font-bold tracking-tight">
                  {openTool.title}
                </DialogTitle>
                <NewPill className={theme.newPill} />
              </div>

              <DialogDescription className={cn("text-[15px] leading-relaxed", theme.modalFeature)}>
                {openTool.blurb}
              </DialogDescription>

              <div className="flex flex-wrap gap-1.5">
                {openTool.fileTags.map((tag) => (
                  <span
                    key={tag}
                    className={cn(
                      "inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide",
                      theme.modalChip,
                    )}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <ul className="space-y-1.5">
                {openTool.features.map((feature) => (
                  <li
                    key={feature}
                    className={cn("flex items-start gap-2 text-sm leading-snug", theme.modalFeature)}
                  >
                    <CheckCircle2 className={cn("mt-0.5 h-4 w-4 flex-none", theme.modalCheck)} />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link
                href={activeLocked ? lockedToolHref(openTool.featureKey) : openTool.href}
                onClick={() => setOpenTool(null)}
                className={cn(
                  "mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold transition",
                  theme.cta,
                )}
              >
                {activeLocked ? (
                  <>
                    <Lock className="h-4 w-4" />
                    {studioLockCopy(openTool.featureKey)?.cta ?? "Upgrade to unlock"}
                  </>
                ) : (
                  <>
                    Open {openTool.title}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Link>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </section>
  );
}
